/**
 * /payouts/hold — operator UI for Feature #138 payout-hold.
 *
 * Composition:
 *   - Affiliate search (free-text affiliate_id input)
 *   - Current hold card: hold_days slider (0-120), reason field, status
 *     (active/paused) toggle, Save button
 *   - Release queue table: conversions past their hold window, with the
 *     earliest-payable date prominently displayed
 *
 * Data layer: TanStack Query for fetch + mutation; Sonner for feedback.
 * Brand: emerald→sky headline gradient, zinc borders, 6–8px rounded, light
 * theme only, no emojis, no competitor names.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	clampHoldDays,
	DEFAULT_HOLD_DAYS,
	fetchPayoutHold,
	fetchReleaseQueue,
	formatDate,
	formatInteger,
	formatMoneyCents,
	isValidAffiliateId,
	MAX_HOLD_DAYS,
	MIN_HOLD_DAYS,
	payoutHoldQueryKeys,
	setPayoutHold,
	type HoldStatus,
	type ReleaseQueueItem,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const TEXTAREA = `${INPUT} resize-none`;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_ACTIVE =
	"inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 border border-emerald-200";
const PILL_PAUSED =
	"inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200";
const PILL_DEFAULT =
	"inline-flex items-center rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 border border-sky-200";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PayoutHoldPageProps {
	initialAffiliateId?: string;
}

export function PayoutHoldPage(props: PayoutHoldPageProps = {}) {
	const queryClient = useQueryClient();

	// Free-text affiliate search. We debounce via commit-on-blur / Enter so
	// we don't hammer the API on every keystroke.
	const [searchInput, setSearchInput] = useState(
		props.initialAffiliateId && isValidAffiliateId(props.initialAffiliateId)
			? props.initialAffiliateId
			: "",
	);
	const [committedAffiliate, setCommittedAffiliate] = useState<string | null>(
		props.initialAffiliateId && isValidAffiliateId(props.initialAffiliateId)
			? props.initialAffiliateId
			: null,
	);

	// Draft slider / reason / status — reconciled against the remote hold
	// whenever we load a new affiliate.
	const [draftHoldDays, setDraftHoldDays] = useState<number>(DEFAULT_HOLD_DAYS);
	const [draftReason, setDraftReason] = useState<string>("");
	const [draftStatus, setDraftStatus] = useState<HoldStatus>("active");

	// ----- Current hold fetch -----
	const holdQuery = useQuery({
		queryKey: committedAffiliate
			? payoutHoldQueryKeys.byAffiliate(committedAffiliate)
			: payoutHoldQueryKeys.byAffiliate("_none"),
		queryFn: () => fetchPayoutHold(committedAffiliate as string),
		enabled: committedAffiliate !== null,
	});

	// Sync remote → draft on each successful load.
	useEffect(() => {
		if (holdQuery.data) {
			setDraftHoldDays(holdQuery.data.hold_days);
			setDraftReason(holdQuery.data.reason ?? "");
			setDraftStatus(holdQuery.data.status);
		}
	}, [holdQuery.data]);

	// ----- Release queue fetch -----
	const queueQuery = useQuery({
		queryKey: payoutHoldQueryKeys.releaseQueue({
			affiliateId: committedAffiliate ?? undefined,
		}),
		queryFn: () =>
			fetchReleaseQueue({
				limit: 100,
				affiliateId: committedAffiliate ?? undefined,
			}),
	});

	// ----- Save mutation -----
	const saveMutation = useMutation({
		mutationFn: setPayoutHold,
		onSuccess: (saved) => {
			toast.success(`Hold updated: ${saved.hold_days} days.`);
			queryClient.invalidateQueries({
				queryKey: payoutHoldQueryKeys.byAffiliate(saved.affiliate_id),
			});
			queryClient.invalidateQueries({
				queryKey: payoutHoldQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to update hold.");
		},
	});

	const hold = holdQuery.data;
	const items: readonly ReleaseQueueItem[] = queueQuery.data?.items ?? [];
	const busy = saveMutation.isPending;

	// ----- Handlers -----
	const handleCommitSearch = useCallback(() => {
		const trimmed = searchInput.trim();
		if (!trimmed) {
			setCommittedAffiliate(null);
			return;
		}
		if (!isValidAffiliateId(trimmed)) {
			toast.error("Affiliate id must be alphanumeric / _ / -, ≤64 chars.");
			return;
		}
		setCommittedAffiliate(trimmed);
	}, [searchInput]);

	const handleSliderChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const n = Number.parseInt(e.target.value, 10);
			setDraftHoldDays(clampHoldDays(Number.isFinite(n) ? n : DEFAULT_HOLD_DAYS));
		},
		[],
	);

	const handleNumberChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const n = Number.parseInt(e.target.value, 10);
			setDraftHoldDays(clampHoldDays(Number.isFinite(n) ? n : DEFAULT_HOLD_DAYS));
		},
		[],
	);

	const handleSave = useCallback(() => {
		if (!committedAffiliate || busy) return;
		saveMutation.mutate({
			affiliateId: committedAffiliate,
			holdDays: draftHoldDays,
			reason: draftReason.trim() || null,
			status: draftStatus,
		});
	}, [committedAffiliate, busy, saveMutation, draftHoldDays, draftReason, draftStatus]);

	const pendingDirty = useMemo(() => {
		if (!hold) return false;
		return (
			hold.hold_days !== draftHoldDays ||
			(hold.reason ?? "") !== draftReason ||
			hold.status !== draftStatus
		);
	}, [hold, draftHoldDays, draftReason, draftStatus]);

	return (
		<div data-testid="payout-hold-page" className="flex flex-col gap-6">
			{/* Heading */}
			<header className="flex flex-col gap-1">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold text-transparent">
					Payout holds
				</h1>
				<p className="text-sm text-zinc-600">
					Configure how long each affiliate's conversions sit in escrow before
					they become payable. Default: {DEFAULT_HOLD_DAYS} days.
				</p>
			</header>

			{/* Affiliate search */}
			<section className={`${CARD} flex flex-col gap-3 p-4`} aria-label="Affiliate search">
				<label className={LABEL} htmlFor="payout-hold-search">
					Affiliate id
				</label>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
					<input
						id="payout-hold-search"
						type="text"
						className={INPUT}
						placeholder="aff_123"
						value={searchInput}
						autoComplete="off"
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleCommitSearch();
							}
						}}
						onBlur={handleCommitSearch}
					/>
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={handleCommitSearch}
					>
						Load
					</button>
				</div>
			</section>

			{/* Current hold card */}
			{committedAffiliate && (
				<section
					className={`${CARD} flex flex-col gap-4 p-4`}
					aria-label="Current hold"
				>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<h2 className="text-base font-semibold text-zinc-900">
							Hold for <span className="font-mono">{committedAffiliate}</span>
						</h2>
						<div className="flex items-center gap-2">
							{hold?.is_default && (
								<span className={PILL_DEFAULT}>Default (not yet saved)</span>
							)}
							{hold && !hold.is_default && hold.status === "active" && (
								<span className={PILL_ACTIVE}>Active</span>
							)}
							{hold && !hold.is_default && hold.status === "paused" && (
								<span className={PILL_PAUSED}>Paused</span>
							)}
						</div>
					</div>

					{holdQuery.isLoading && (
						<div className="text-sm text-zinc-500">Loading hold…</div>
					)}
					{holdQuery.isError && (
						<div className="text-sm text-red-600">
							Failed to load hold configuration.
						</div>
					)}

					<div className="flex flex-col gap-3">
						<div className="flex items-center justify-between gap-4">
							<label className={LABEL} htmlFor="payout-hold-slider">
								Hold days
							</label>
							<span
								className="font-mono text-sm text-zinc-900"
								data-testid="hold-days-value"
							>
								{draftHoldDays} days
							</span>
						</div>
						<input
							id="payout-hold-slider"
							type="range"
							min={MIN_HOLD_DAYS}
							max={MAX_HOLD_DAYS}
							step={1}
							value={draftHoldDays}
							onChange={handleSliderChange}
							className="w-full accent-emerald-600"
							aria-valuemin={MIN_HOLD_DAYS}
							aria-valuemax={MAX_HOLD_DAYS}
							aria-valuenow={draftHoldDays}
						/>
						<div className="flex items-center justify-between text-xs text-zinc-500">
							<span>{MIN_HOLD_DAYS}</span>
							<span>{MAX_HOLD_DAYS}</span>
						</div>
						<div className="flex items-center gap-2">
							<input
								id="payout-hold-number"
								type="number"
								min={MIN_HOLD_DAYS}
								max={MAX_HOLD_DAYS}
								value={draftHoldDays}
								onChange={handleNumberChange}
								className={`${INPUT} max-w-[6rem]`}
								aria-label="Hold days numeric input"
							/>
							<span className="text-xs text-zinc-500">
								Range: {MIN_HOLD_DAYS}–{MAX_HOLD_DAYS} days.
							</span>
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<label className={LABEL} htmlFor="payout-hold-reason">
							Reason (optional)
						</label>
						<textarea
							id="payout-hold-reason"
							className={TEXTAREA}
							rows={2}
							maxLength={500}
							value={draftReason}
							onChange={(e) => setDraftReason(e.target.value)}
							placeholder="e.g. new affiliate pilot, fraud escalation, etc."
						/>
					</div>

					<div className="flex items-center gap-4">
						<label className={LABEL} htmlFor="payout-hold-status">
							Status
						</label>
						<select
							id="payout-hold-status"
							className={INPUT}
							value={draftStatus}
							onChange={(e) => setDraftStatus(e.target.value as HoldStatus)}
						>
							<option value="active">Active (payouts honored)</option>
							<option value="paused">Paused (exclude from queue)</option>
						</select>
					</div>

					<div className="flex items-center justify-end gap-2">
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={handleSave}
							disabled={busy || !committedAffiliate}
							data-testid="save-hold-btn"
						>
							{busy ? "Saving…" : pendingDirty ? "Save changes" : "Save"}
						</button>
					</div>
				</section>
			)}

			{/* Release queue */}
			<section className={`${CARD} flex flex-col gap-3 p-4`} aria-label="Release queue">
				<div className="flex items-center justify-between gap-2">
					<h2 className="text-base font-semibold text-zinc-900">
						Release queue
						{committedAffiliate && (
							<span className="ml-2 font-mono text-sm text-zinc-500">
								({committedAffiliate})
							</span>
						)}
					</h2>
					<span className="text-sm text-zinc-500">
						{formatInteger(items.length)} conversion
						{items.length === 1 ? "" : "s"} ready
					</span>
				</div>

				{queueQuery.isLoading && (
					<div className="text-sm text-zinc-500">Loading queue…</div>
				)}
				{queueQuery.isError && (
					<div className="text-sm text-red-600">
						Failed to load release queue.
					</div>
				)}

				{!queueQuery.isLoading && items.length === 0 && (
					<div className="rounded-[6px] border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center text-sm text-zinc-600">
						No conversions are past their hold window yet.
					</div>
				)}

				{items.length > 0 && (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50">
								<tr>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Conversion
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Affiliate
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Hold
									</th>
									<th className="px-3 py-2 text-left font-medium text-zinc-600">
										Earliest payable
									</th>
									<th className="px-3 py-2 text-right font-medium text-zinc-600">
										Payout
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => (
									<tr key={row.conversion_id} data-testid="queue-row">
										<td className="px-3 py-2 font-mono text-xs text-zinc-900">
											{row.conversion_id}
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.affiliate_id}
										</td>
										<td className="px-3 py-2 text-zinc-700">
											{row.hold_days}d
										</td>
										<td className="px-3 py-2 text-zinc-700">
											{formatDate(row.earliest_payable_ts)}
										</td>
										<td className="px-3 py-2 text-right font-medium text-zinc-900">
											{formatMoneyCents(row.payout_cents, row.currency)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</div>
	);
}

export default PayoutHoldPage;
